import type {MRT_ColumnFiltersState, MRT_SortingState} from 'mantine-react-table';
import React from 'react';
import type {ProjectSimpleResponse} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useGetProjectListV2Query} from 'src/redux/queries/project';
import {useProjectColumns} from './hooks/useProjectColumns';

export interface ProjectTableProps {
  identifier: string;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;
  onViewRowClick: (row: ProjectSimpleResponse) => void;
  onCreate?: () => void;
}

export const ProjectTable: React.FC<ProjectTableProps> = ({
  identifier,
  defaultSorting,
  defaultColumnFilters,
  onViewRowClick,
  onCreate,
}) => {
  const columns = useProjectColumns();

  const tableState = useTableState({
    identifier: identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: defaultSorting ?? [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
    defaultColumnFilters: defaultColumnFilters ?? [],
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const params = {
    start: start,
    length: length,
    filters: filters,
    filterFns: filterFns,
    sorting: sorting,
  };
  const {data, isLoading, isError, isFetching, refetch} =
    useGetProjectListV2Query(params, {refetchOnMountOrArgChange: true});

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View onClick={() => onViewRowClick(row.original)} />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {onCreate && (
              <TableToolbar.Action variant="create" onClick={onCreate}>
                New Project
              </TableToolbar.Action>
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
